import React, { useEffect, useMemo, useState } from "react";
import {
  Truck,
  RefreshCw,
  Mail,
  CheckCircle2,
  XCircle,
  Download,
  Search,
  ExternalLink,
  Copy,
  Package as PackageIcon,
  ChevronDown,
  ChevronUp,
  TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatPrice } from "../lib/api";
import AdminLayout from "../components/AdminLayout";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "confirmed", label: "Paid" },
  { key: "shipped", label: "Shipped" },
  { key: "delivered", label: "Delivered" },
  { key: "refunded", label: "Refunded" },
  { key: "cancelled", label: "Cancelled" },
];

const STATUS_STYLES = {
  confirmed: "bg-[var(--mn-ink)] text-[var(--mn-bg)]",
  shipped: "bg-emerald-700 text-white",
  delivered: "bg-emerald-900 text-white",
  refunded: "bg-[var(--mn-accent)] text-white",
  cancelled: "bg-neutral-400 text-white",
};

const CARRIERS = ["UPS", "FedEx", "USPS", "DHL", "Other"];

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [shipModal, setShipModal] = useState(null); // order
  const [refundModal, setRefundModal] = useState(null); // order

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (filter !== "all") params.status = filter;
      if (search.trim()) params.q = search.trim();
      const r = await api.get("/orders", { params });
      setOrders(r.data || []);
      setSelected(new Set());
    } catch (_) {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const filterCounts = useMemo(() => {
    const counts = { all: orders.length };
    for (const o of orders) counts[o.status] = (counts[o.status] || 0) + 1;
    return counts;
  }, [orders]);

  const last30 = useMemo(() => buildRevenueSeries(orders, 30), [orders]);

  const toggleSelect = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };

  const bulkShip = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Mark ${selected.size} order(s) as shipped (without tracking number)? Customers will NOT be emailed.`)) return;
    try {
      const r = await api.post("/orders/bulk/ship", { order_ids: Array.from(selected) });
      toast.success(`${r.data.modified} order(s) marked shipped.`);
      load();
    } catch (_) {
      toast.error("Bulk update failed.");
    }
  };

  const exportCSV = async () => {
    try {
      const r = await api.get("/orders/export/csv", { responseType: "blob" });
      const filename = `elegant-affairs-orders-${new Date().toISOString().slice(0, 10)}.csv`;
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1500);
      toast.success("Orders CSV downloaded.");
    } catch (_) {
      toast.error("CSV export failed.");
    }
  };

  const updateStatus = async (orderId, status) => {
    try {
      await api.patch(`/orders/${orderId}/status`, { status });
      toast.success(`Order marked ${status}.`);
      load();
    } catch (_) {
      toast.error("Status update failed.");
    }
  };

  const resendConfirmation = async (orderId) => {
    try {
      await api.post(`/orders/${orderId}/resend-confirmation`);
      toast.success("Confirmation email resent.");
    } catch (_) {
      toast.error("Could not resend email.");
    }
  };

  return (
    <AdminLayout
      title="Orders."
      action={
        <div className="flex flex-wrap items-center gap-2">
          {selected.size > 0 && (
            <button onClick={bulkShip} className="btn-ghost" data-testid="orders-bulk-ship">
              <Truck size={14} strokeWidth={1.5} /> Ship {selected.size} selected
            </button>
          )}
          <button onClick={exportCSV} className="btn-ghost" data-testid="orders-export-csv">
            <Download size={14} strokeWidth={1.5} /> Export CSV
          </button>
        </div>
      }
    >
      <div data-testid="admin-orders-page">
        {/* Revenue chart */}
        <div className="mb-12 p-6 border border-[var(--mn-border)]" data-testid="orders-revenue-chart">
          <div className="flex items-center justify-between">
            <div>
              <p className="eyebrow">Last 30 days</p>
              <h2 className="font-display text-3xl mt-1 tracking-tighter">{formatPrice(last30.total)}</h2>
              <p className="text-xs text-[var(--mn-ink-2)] mt-1">
                {last30.orderCount} order{last30.orderCount === 1 ? "" : "s"} · avg {formatPrice(last30.avg)}
              </p>
            </div>
            <TrendingUp size={20} strokeWidth={1.4} className="text-[var(--mn-ink-2)]" />
          </div>
          <div className="mt-6">
            <BarChart series={last30.series} />
          </div>
        </div>

        {/* Search + filter bar */}
        <div className="flex flex-col lg:flex-row gap-4 mb-8">
          <div className="flex-1 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--mn-ink-2)]" strokeWidth={1.5} />
            <input
              type="text"
              placeholder="Search by order #, customer name, or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              className="field-input pl-10"
              data-testid="orders-search"
            />
          </div>
          <button onClick={load} className="btn-ghost" data-testid="orders-search-go">
            <Search size={14} strokeWidth={1.5} /> Search
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mb-8" data-testid="orders-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-4 py-2 text-xs uppercase tracking-[0.22em] border ${
                filter === f.key
                  ? "border-[var(--mn-ink)] bg-[var(--mn-ink)] text-[var(--mn-bg)]"
                  : "border-[var(--mn-border)] text-[var(--mn-ink-2)] hover:border-[var(--mn-ink)] hover:text-[var(--mn-ink)]"
              }`}
              data-testid={`filter-${f.key}`}
            >
              {f.label}{filterCounts[f.key] !== undefined ? ` · ${filterCounts[f.key]}` : ""}
            </button>
          ))}
        </div>

        {/* Order list */}
        {loading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-24 bg-neutral-200/60 animate-pulse" />
            ))}
          </div>
        ) : orders.length === 0 ? (
          <div className="border border-dashed border-[var(--mn-border-strong)] py-32 text-center" data-testid="orders-empty">
            <PackageIcon size={32} strokeWidth={1.2} className="mx-auto text-[var(--mn-ink-2)]" />
            <p className="text-[var(--mn-ink-2)] mt-4">No orders match the filter.</p>
          </div>
        ) : (
          <ul className="border-y border-[var(--mn-border)] divide-y divide-[var(--mn-border)]" data-testid="orders-list">
            {orders.map((o) => (
              <OrderRow
                key={o.id}
                order={o}
                isOpen={openId === o.id}
                onToggle={() => setOpenId(openId === o.id ? null : o.id)}
                isSelected={selected.has(o.id)}
                onSelect={() => toggleSelect(o.id)}
                onShip={() => setShipModal(o)}
                onRefund={() => setRefundModal(o)}
                onResendConfirm={() => resendConfirmation(o.id)}
                onUpdateStatus={(s) => updateStatus(o.id, s)}
              />
            ))}
          </ul>
        )}
      </div>

      {shipModal && (
        <ShipModal order={shipModal} onClose={() => setShipModal(null)} onShipped={() => { setShipModal(null); load(); }} />
      )}
      {refundModal && (
        <RefundModal order={refundModal} onClose={() => setRefundModal(null)} onRefunded={() => { setRefundModal(null); load(); }} />
      )}
    </AdminLayout>
  );
}

// ----------------------------------------------------------------
// Row
// ----------------------------------------------------------------
function OrderRow({ order: o, isOpen, onToggle, isSelected, onSelect, onShip, onRefund, onResendConfirm, onUpdateStatus }) {
  const carrier = o.tracking_carrier || "";
  const number = o.tracking_number || "";
  return (
    <li className="py-5" data-testid={`order-${o.id}`}>
      <div className="flex items-start gap-4">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onSelect}
          onClick={(e) => e.stopPropagation()}
          className="mt-2 cursor-pointer"
          data-testid={`order-select-${o.id}`}
          aria-label="Select order"
        />
        <button onClick={onToggle} className="flex-1 text-left" data-testid={`order-toggle-${o.id}`}>
          <div className="flex items-start justify-between gap-6">
            <div className="min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-display text-xl tracking-tight">{o.order_number}</span>
                <span className={`px-2 py-1 text-[10px] uppercase tracking-[0.22em] ${STATUS_STYLES[o.status] || "bg-neutral-200"}`} data-testid={`order-status-${o.id}`}>
                  {o.status}
                </span>
                {o.payment_status === "refunded" && (
                  <span className="px-2 py-1 text-[10px] uppercase tracking-[0.22em] bg-[var(--mn-accent)] text-white">
                    refunded
                  </span>
                )}
              </div>
              <p className="text-xs text-[var(--mn-ink-2)] mt-1.5">
                {o.shipping?.full_name} · {o.shipping?.email} · {o.items.length} item{o.items.length === 1 ? "" : "s"}
              </p>
            </div>
            <div className="text-right shrink-0">
              <p className="font-mono-luxe text-base">{formatPrice(o.total)}</p>
              <p className="text-[10px] uppercase tracking-[0.22em] text-[var(--mn-muted)] mt-1">
                {timeAgo(o.created_at)}
              </p>
            </div>
            <span className="mt-1">{isOpen ? <ChevronUp size={16} strokeWidth={1.5} /> : <ChevronDown size={16} strokeWidth={1.5} />}</span>
          </div>
        </button>
      </div>

      {isOpen && (
        <div className="mt-6 pl-9 grid grid-cols-1 md:grid-cols-3 gap-8" data-testid={`order-details-${o.id}`}>
          {/* Items */}
          <div>
            <p className="eyebrow mb-3">Items</p>
            <ul className="space-y-2 text-sm">
              {o.items.map((it, i) => (
                <li key={i} className="flex justify-between gap-3">
                  <span className="text-[var(--mn-ink)]">{it.name} <span className="text-[var(--mn-ink-2)]">×{it.quantity}</span></span>
                  <span className="font-mono-luxe text-[var(--mn-ink-2)]">{formatPrice(it.price * it.quantity)}</span>
                </li>
              ))}
            </ul>
            <div className="mt-4 pt-3 border-t border-[var(--mn-border)] space-y-1 text-xs">
              <div className="flex justify-between"><span className="text-[var(--mn-ink-2)]">Subtotal</span><span className="font-mono-luxe">{formatPrice(o.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-[var(--mn-ink-2)]">Shipping</span><span className="font-mono-luxe">{o.shipping_cost ? formatPrice(o.shipping_cost) : "Free"}</span></div>
              <div className="flex justify-between"><span className="text-[var(--mn-ink-2)]">Tax</span><span className="font-mono-luxe">{formatPrice(o.tax)}</span></div>
              <div className="flex justify-between font-semibold pt-1 border-t border-[var(--mn-border)] mt-1"><span>Total</span><span className="font-mono-luxe">{formatPrice(o.total)}</span></div>
              {o.refund_amount > 0 && (
                <div className="flex justify-between text-[var(--mn-accent)] pt-1"><span>Refunded</span><span className="font-mono-luxe">−{formatPrice(o.refund_amount)}</span></div>
              )}
            </div>
          </div>

          {/* Shipping address */}
          <div>
            <p className="eyebrow mb-3">Ship to</p>
            <div className="text-sm leading-relaxed">
              <p className="font-medium">{o.shipping?.full_name}</p>
              <p>{o.shipping?.address_line1}</p>
              {o.shipping?.address_line2 && <p>{o.shipping.address_line2}</p>}
              <p>{o.shipping?.city}{o.shipping?.state ? `, ${o.shipping.state}` : ""} {o.shipping?.postal_code}</p>
              <p>{o.shipping?.country}</p>
              <p className="mt-2 text-[var(--mn-ink-2)]">
                <a href={`mailto:${o.shipping?.email}`} className="underline">{o.shipping?.email}</a>
              </p>
              {o.shipping?.phone && <p className="text-[var(--mn-ink-2)]">{o.shipping.phone}</p>}
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              <button
                onClick={() => copyShippingForBG(o)}
                className="btn-ghost !px-3 !py-2 text-[10px]"
                data-testid={`order-copy-shipping-${o.id}`}
                title="Copy shipping address formatted for BrandsGateway"
              >
                <Copy size={12} strokeWidth={1.5} /> Copy for BG
              </button>
              <a
                href="https://brandsgateway.com"
                target="_blank"
                rel="noreferrer"
                className="btn-ghost !px-3 !py-2 text-[10px]"
                data-testid={`order-open-bg-${o.id}`}
                title="Open BrandsGateway in a new tab"
              >
                <ExternalLink size={12} strokeWidth={1.5} /> Open BG
              </a>
            </div>
          </div>

          {/* Tracking + Actions */}
          <div>
            <p className="eyebrow mb-3">Fulfillment</p>
            {number ? (
              <div className="text-sm space-y-1.5">
                <p><span className="text-[var(--mn-ink-2)]">Carrier:</span> {carrier || "—"}</p>
                <p><span className="text-[var(--mn-ink-2)]">Tracking:</span> <span className="font-mono-luxe">{number}</span></p>
                {o.shipped_at && <p className="text-xs text-[var(--mn-muted)]">Shipped {new Date(o.shipped_at).toLocaleDateString()}</p>}
              </div>
            ) : (
              <p className="text-sm text-[var(--mn-ink-2)]">No tracking yet.</p>
            )}
            <div className="flex flex-col gap-2 mt-4">
              {o.status !== "shipped" && o.status !== "delivered" && o.status !== "refunded" && (
                <button onClick={onShip} className="btn-primary !text-[11px]" data-testid={`order-ship-${o.id}`}>
                  <Truck size={12} strokeWidth={1.5} /> Mark Shipped
                </button>
              )}
              {o.status === "shipped" && (
                <button onClick={() => onUpdateStatus("delivered")} className="btn-ghost !text-[10px]" data-testid={`order-mark-delivered-${o.id}`}>
                  <CheckCircle2 size={12} strokeWidth={1.5} /> Mark Delivered
                </button>
              )}
              <button onClick={onResendConfirm} className="btn-ghost !text-[10px]" data-testid={`order-resend-${o.id}`}>
                <Mail size={12} strokeWidth={1.5} /> Resend Confirmation
              </button>
              {o.payment_status !== "refunded" && o.status !== "cancelled" && o.stripe_payment_intent && (
                <button onClick={onRefund} className="btn-ghost !text-[10px] hover:!border-[var(--mn-accent)] hover:!text-[var(--mn-accent)]" data-testid={`order-refund-${o.id}`}>
                  <RefreshCw size={12} strokeWidth={1.5} /> Refund
                </button>
              )}
              {o.status !== "cancelled" && o.status !== "shipped" && o.status !== "delivered" && (
                <button onClick={() => { if (window.confirm("Cancel this order? (No refund will be issued automatically.)")) onUpdateStatus("cancelled"); }} className="btn-ghost !text-[10px] !text-[var(--mn-ink-2)]" data-testid={`order-cancel-${o.id}`}>
                  <XCircle size={12} strokeWidth={1.5} /> Cancel Order
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </li>
  );
}

// ----------------------------------------------------------------
// Modals
// ----------------------------------------------------------------
function ShipModal({ order, onClose, onShipped }) {
  const [carrier, setCarrier] = useState("UPS");
  const [number, setNumber] = useState("");
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!number.trim()) { toast.error("Tracking number required."); return; }
    setBusy(true);
    try {
      await api.post(`/orders/${order.id}/ship`, {
        carrier, tracking_number: number.trim(), notify_customer: notify,
      });
      toast.success(notify ? "Marked shipped + tracking email sent." : "Marked shipped.");
      onShipped();
    } catch (err) {
      toast.error("Failed to mark shipped.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-[60]" onClick={onClose} data-testid="ship-modal-backdrop" />
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-6 pointer-events-none">
        <form onSubmit={submit} className="bg-[var(--mn-bg)] w-full max-w-md pointer-events-auto border border-[var(--mn-border)]" data-testid="ship-modal">
          <div className="px-8 py-6 border-b border-[var(--mn-border)]">
            <p className="eyebrow">Fulfillment</p>
            <h2 className="font-display text-3xl mt-1 tracking-tight">Mark Shipped</h2>
            <p className="text-xs text-[var(--mn-ink-2)] mt-1">Order {order.order_number}</p>
          </div>
          <div className="px-8 py-6 space-y-4">
            <label className="block">
              <span className="eyebrow block mb-2">Carrier</span>
              <select value={carrier} onChange={(e) => setCarrier(e.target.value)} className="field-input" data-testid="ship-carrier">
                {CARRIERS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="eyebrow block mb-2">Tracking Number</span>
              <input
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                placeholder="1Z999AA10123456784"
                className="field-input font-mono-luxe"
                data-testid="ship-tracking"
                autoFocus
              />
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} className="mt-1" data-testid="ship-notify" />
              <span className="text-sm text-[var(--mn-ink-2)]">
                Email <strong className="text-[var(--mn-ink)]">{order.shipping?.email}</strong> with the tracking link
              </span>
            </label>
          </div>
          <div className="px-8 py-6 border-t border-[var(--mn-border)] flex justify-end gap-3">
            <button type="button" onClick={onClose} className="btn-ghost" data-testid="ship-cancel">Cancel</button>
            <button type="submit" disabled={busy} className="btn-primary" data-testid="ship-submit">
              {busy ? "Saving…" : "Mark Shipped"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

function RefundModal({ order, onClose, onRefunded }) {
  const [amount, setAmount] = useState(String(order.total));
  const [reason, setReason] = useState("");
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const a = Number(amount);
    if (!Number.isFinite(a) || a <= 0 || a > order.total) {
      toast.error("Enter a valid amount up to the order total.");
      return;
    }
    if (!window.confirm(`Refund ${formatPrice(a)} via Stripe? This is irreversible.`)) return;
    setBusy(true);
    try {
      await api.post(`/orders/${order.id}/refund`, { amount: a, reason, notify_customer: notify });
      toast.success(`Refund of ${formatPrice(a)} issued.`);
      onRefunded();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Refund failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-[60]" onClick={onClose} data-testid="refund-modal-backdrop" />
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-6 pointer-events-none">
        <form onSubmit={submit} className="bg-[var(--mn-bg)] w-full max-w-md pointer-events-auto border border-[var(--mn-border)]" data-testid="refund-modal">
          <div className="px-8 py-6 border-b border-[var(--mn-border)]">
            <p className="eyebrow text-[var(--mn-accent)]">Refund</p>
            <h2 className="font-display text-3xl mt-1 tracking-tight">Issue Refund</h2>
            <p className="text-xs text-[var(--mn-ink-2)] mt-1">Order {order.order_number} · Total {formatPrice(order.total)}</p>
          </div>
          <div className="px-8 py-6 space-y-4">
            <label className="block">
              <span className="eyebrow block mb-2">Refund Amount (USD)</span>
              <input
                type="number"
                step="0.01"
                min="0.01"
                max={order.total}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="field-input font-mono-luxe"
                data-testid="refund-amount"
                autoFocus
              />
              <p className="text-xs text-[var(--mn-ink-2)] mt-1">Max {formatPrice(order.total)} · partial refunds allowed</p>
            </label>
            <label className="block">
              <span className="eyebrow block mb-2">Reason (internal note)</span>
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Customer return, damaged, etc."
                className="field-input"
                data-testid="refund-reason"
              />
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} className="mt-1" data-testid="refund-notify" />
              <span className="text-sm text-[var(--mn-ink-2)]">
                Email customer with refund confirmation
              </span>
            </label>
          </div>
          <div className="px-8 py-6 border-t border-[var(--mn-border)] flex justify-end gap-3">
            <button type="button" onClick={onClose} className="btn-ghost" data-testid="refund-cancel">Cancel</button>
            <button type="submit" disabled={busy} className="btn-primary !bg-[var(--mn-accent)] !border-[var(--mn-accent)]" data-testid="refund-submit">
              {busy ? "Refunding…" : "Issue Refund"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

// ----------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------
function copyShippingForBG(o) {
  const s = o.shipping || {};
  const lines = [
    s.full_name,
    s.address_line1,
    s.address_line2,
    `${s.city || ""}${s.state ? ", " + s.state : ""} ${s.postal_code || ""}`.trim(),
    s.country,
    s.phone ? `Phone: ${s.phone}` : "",
    s.email ? `Email: ${s.email}` : "",
  ].filter(Boolean).join("\n");
  navigator.clipboard.writeText(lines).then(
    () => toast.success("Shipping address copied — paste into BrandsGateway."),
    () => toast.error("Could not copy.")
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

function buildRevenueSeries(orders, days) {
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  const buckets = [];
  const dayMs = 24 * 60 * 60 * 1000;
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today.getTime() - i * dayMs);
    buckets.push({ date: d, key: d.toISOString().slice(0, 10), revenue: 0, count: 0 });
  }
  const idx = Object.fromEntries(buckets.map((b, i) => [b.key, i]));
  let total = 0;
  let orderCount = 0;
  for (const o of orders) {
    if (o.status === "cancelled") continue;
    const k = (o.created_at || "").slice(0, 10);
    if (k in idx) {
      const i = idx[k];
      const net = (o.total || 0) - (o.refund_amount || 0);
      buckets[i].revenue += net;
      buckets[i].count += 1;
      total += net;
      orderCount += 1;
    }
  }
  return {
    series: buckets,
    total,
    orderCount,
    avg: orderCount ? total / orderCount : 0,
  };
}

function BarChart({ series, height = 60 }) {
  const max = Math.max(...series.map((b) => b.revenue), 1);
  return (
    <div className="flex items-end gap-1" style={{ height }} data-testid="revenue-bars">
      {series.map((b, i) => {
        const h = Math.max(2, (b.revenue / max) * height);
        return (
          <div
            key={i}
            className="flex-1 bg-[var(--mn-ink)]/80 hover:bg-[var(--mn-ink)] transition-colors"
            style={{ height: h }}
            title={`${b.key}: ${b.revenue ? `$${b.revenue.toFixed(0)}` : "no orders"} (${b.count})`}
          />
        );
      })}
    </div>
  );
}
